/**
 * @module PageBoundaryFallback
 * @package @geenius/adapters/solidjs
 * @description Shared page-level error recovery UI for SolidJS adapter pages.
 * Mirrors the React `PageErrorBoundary` and solidjs-css `PageBoundaryFallback`
 * to satisfy UI_PROTOCOL.md §2 page-boundary recovery requirements.
 */

import type { JSX } from "solid-js";
import { cn } from "../lib/utils";

interface PageBoundaryFallbackProps {
  class?: string;
  error: unknown;
  onRetry: () => void;
  title: string;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "The adapter page could not finish rendering.";
}

export function PageBoundaryFallback(
  props: PageBoundaryFallbackProps,
): JSX.Element {
  return (
    <div class={cn("mx-auto max-w-3xl space-y-4", props.class)}>
      <div
        class="rounded-lg border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive shadow-sm"
        role="alert"
        aria-live="assertive"
      >
        <p class="font-semibold">{props.title}</p>
        <p class="mt-1">{getErrorMessage(props.error)}</p>
        <button
          type="button"
          onClick={() => props.onRetry()}
          class="mt-4 rounded-md border border-destructive/30 bg-background px-4 py-2 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
